import React from 'react';
import { makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { ToasterState } from 'src/AppState/ToasterState';
import { BonusCreationPatchType, BonusCreationStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { BonusCreationRouteType, BonusCreationViewType } from 'src/domains/casino/models/models';
import { BonusCreationStore } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreation.store';
import { BonusCreationModel } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreationModel';
import { GeneralDetailsState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/general-details/GeneralDetails.state';
import {
    BonusCreationFormZodType,
    isError,
    isInvalidUpdateBonusCreationRes,
} from 'src/domains/casino/webview/bonus-engine/common/helpers';
import { RewardConditionsState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/reward-conditions/RewardConditions.state';

import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { z } from 'zod';
import { PopupsStackState } from 'src/AppState/PopupsStackState';

export function zodGuard<T>(value: unknown, parser: z.ZodType<T>): T {
    const result = parser.safeParse(value);
    if (!result.success) {
        throw new Error(`Invalid data: ${result.error.message}`);
    }
    return result.data;
}

export class BonusCreationState {
    public isLoading: boolean = false;
    public readonly generalDetailsState: GeneralDetailsState;
    public readonly rewardConditionsState: RewardConditionsState;

    private readonly bonusCreationStore: BonusCreationStore;

    public isFilledData: boolean = false;

    public static get = AutoWeakMap.create(
        (common: Common, bonusCreationId: string | undefined) => new BonusCreationState(common, bonusCreationId)
    );

    private updateBonusCreationRes: unknown;

    private constructor(
        private readonly common: Common,
        private readonly bonusCreationId: string | undefined
    ) {
        this.bonusCreationStore = BonusCreationStore.get(this.common);

        this.generalDetailsState = new GeneralDetailsState(this.common, this.bonusCreationId);

        this.rewardConditionsState = new RewardConditionsState(this.common, this.bonusCreationId);

        makeAutoObservable(this);
    }

    public fillDataIf = async (): Promise<void> => {
        if (this.isFilledData === false || this.isFormTouched()) {
            await this.fillData();
        }
    };

    private fillData = async (): Promise<void> => {
        this.isLoading = true;

        if (this.bonusCreation === null) {
            this.isLoading = false;
            return;
        }

        await this.generalDetailsState.setFormValues(this.bonusCreation);
        await this.rewardConditionsState.setFormValues(this.bonusCreation);

        this.isLoading = false;

        this.isFilledData = true;
        this.resetIsFormTouched();
    };

    public get bonusRewardTabsErrors(): Record<BonusCreationViewType, number> {
        return {
            'general-details': this.generalDetailsState.formErrorsCount,
            'reward-conditions': this.rewardConditionsState.formErrorsCount,
        };
    }

    public createBonusCreation = async (
        status: 'draft' | 'published',
        route: BonusCreationRouteType | null
    ): Promise<void> => {
        this.validateBonusCreationForm(status);
        const data = BonusCreationFormZodType.parse({
            ...(route?.type !== 'NEW' && { bonusCreationId: this.bonusCreationId }),
            status: status,
            name: this.generalDetailsState.name,
            terms: this.generalDetailsState.termsAndConditions,
            ...this.rewardConditionsState.rewardConditions,
        });
        this.isLoading = true;
        try {
            const res = await this.bonusCreationStore.createBonusCreation(data);
            if (res.status === 201) {
                ToasterState.get(this.common).setToast({
                    variant: 'success',
                    title: 'Success!',
                    message:
                        status === 'draft'
                            ? 'The bonus creation has been successfully created as draft.'
                            : 'The bonus creation has been successfully published.',
                });
                this.resetIsFormVisited();
                $appState.routerBackoffice.redirectToBonusCreator();
            } else {
                this.showErrorToaster('create', res);
            }
        } catch (_error) {
            this.showErrorToaster('create', _error);
        }
        this.isLoading = false;
        this.setIsFormTouched();
    };

    public confirmUnpublishBonusCreation = async (): Promise<void> => {
        const popupsStack = PopupsStackState.get(this.common);

        popupsStack.addPopup('bonus_creation_unpublish', {
            hasOverlay: true,
            template: 'generic',
            title: 'Confirm Unpublish',
            delete_label: 'Unpublish',
            titleClassName: 'text-center font-bold',
            cancel_label: 'Cancel',
            message: (
                <span className='text-pretty'>
                    Please confirm that this bonus is not currently being used in any Optimove campaign before
                    unpublishing.
                </span>
            ),
            onDelete: async () => {
                popupsStack.closePopupOnTop();
                await this.unpublishBonusCreation();
            },
            onCancel: async () => {
                popupsStack.closePopupOnTop();
            },
        });
    };

    private unpublishBonusCreation = async (): Promise<void> => {
        if (this.bonusCreationId === undefined) return;

        this.isLoading = true;
        try {
            const status = await this.bonusCreationStore.unpublishBonusCreation(this.bonusCreationId);
            if (status === 200) {
                ToasterState.get(this.common).setToast({
                    variant: 'success',
                    title: 'Success!',
                    message: `The bonus creation has been successfully unpublished.`,
                });
            } else {
                this.showErrorToaster('unpublish', 'Error unpublishing bonus creation');
            }
        } catch (_error) {
            this.showErrorToaster('unpublish', _error);
        }
        this.isLoading = false;
    };

    public updateBonusCreation = async (): Promise<void> => {
        if (this.bonusCreation?.status !== 'draft') return;

        this.validateBonusCreationForm(this.bonusCreation.status);
        const data: BonusCreationPatchType = {
            name: this.generalDetailsState.name,
            terms: this.generalDetailsState.termsAndConditions,
            ...this.rewardConditionsState.rewardConditions,
        };
        this.isLoading = true;

        try {
            this.updateBonusCreationRes = await this.bonusCreationStore.updateBonusCreation({
                id: this.bonusCreation.id,
                requestBody: data,
            });

            if (isInvalidUpdateBonusCreationRes(this.updateBonusCreationRes) === false) {
                ToasterState.get(this.common).setToast({
                    variant: 'success',
                    title: 'Success!',
                    message: 'The bonus has been successfully updated.',
                });

                this.resetIsFormVisited();
            } else {
                this.showErrorToaster('update', this.updateBonusCreationRes);
            }
        } catch (_error) {
            this.updateBonusCreationRes = _error;
            this.showErrorToaster('update', _error);
        }
        this.isLoading = false;
        this.resetIsFormTouched();
    };

    public activateBonusFromList = async (): Promise<void> => {
        if (this.isFilledData === false) {
            await this.fillData();
        }
        await this.createBonusCreation('published', null);
    };

    public get editionDisabled(): boolean {
        const isCloneBonusCreation = $appState.routerBackoffice.bonusCampaignCloneIdParam !== null;
        return this.bonusCreation?.status === 'published' && !isCloneBonusCreation;
    }

    public get bonusCreation(): BonusCreationModel | null {
        if (this.bonusCreationId === undefined) return null;

        try {
            return this.bonusCreationStore.getBonusCreation(this.bonusCreationId);
        } catch (_error) {
            this.showErrorToaster('fetch', _error);
            return null;
        }
    }

    private validateBonusCreationForm(status: BonusCreationStatusType): void {
        let validGeneralDetails = true;
        let validRewardConditions = true;

        if (status === 'published') {
            validGeneralDetails = this.generalDetailsState.validateForm();
            validRewardConditions = this.rewardConditionsState.validateForm();
        }

        const isInvalid = validGeneralDetails === false || validRewardConditions === false;

        if (isInvalid) {
            throw ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'The bonus could not be saved. Check if all fields are filled out correctly.',
            });
        }
    }

    public isFormTouched(): boolean {
        let isTouchedGeneralDetails = false;
        let isTouchedRewardConditions = false;

        isTouchedGeneralDetails = this.generalDetailsState.isTouched();
        isTouchedRewardConditions = this.rewardConditionsState.isTouched();

        return isTouchedGeneralDetails || isTouchedRewardConditions;
    }

    public setIsFormTouched(): void {
        this.generalDetailsState.setIsTouched();
        this.rewardConditionsState.setIsTouched();
    }

    public resetIsFormTouched(): void {
        this.generalDetailsState.resetIsTouched();
        this.rewardConditionsState.resetIsTouched();
    }

    public resetIsFormVisited(): void {
        this.generalDetailsState.resetIsVisited();
        this.rewardConditionsState.resetIsVisited();
    }

    public showErrorToaster(mode: 'create' | 'update' | 'publish' | 'unpublish' | 'fetch', source: unknown): void {
        const modeToStr = mode === 'fetch' || mode === 'publish' || mode === 'unpublish' ? `${mode}ed` : `${mode}d`;

        const isServerError = isError(source);

        let message = String(source);

        if (isServerError) {
            message = source.message;
        } else if (isInvalidUpdateBonusCreationRes(source)) {
            message = source.body.message;
        }

        ToasterState.get(this.common).setToast({
            variant: 'error',
            title: 'Error!',
            message: (
                <>
                    <p>
                        The bonus could not be {modeToStr}. {isServerError ? 'Server' : 'An'} error has occurred.
                    </p>
                    {typeof message === 'string' ? <p>{message}</p> : null}
                </>
            ),
        });
    }
}
